<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import GowaAPI from 'dashboard/api/gowa';
import Button from 'next/button/Button.vue';
import { useMessageContext } from '../../provider';
import { MESSAGE_TYPES } from '../../constants';
import { groupInviteCodes, groupInviteCodeFromUrl } from './groupInvite';

const {
  content,
  contentAttributes,
  conversationId,
  id,
  inboxId,
  messageType,
  isPrivate,
} = useMessageContext();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(inboxId.value) || {});
const enabled = computed(
  () =>
    window.chatwootConfig?.gowaEnabled &&
    inbox.value.additional_attributes?.gowa?.provisioned &&
    messageType.value === MESSAGE_TYPES.INCOMING &&
    !isPrivate.value
);
const codes = computed(() =>
  enabled.value ? groupInviteCodes(content.value) : []
);
const busy = ref('');
const results = ref({});
const errors = ref({});
const receipt = code =>
  results.value[code] ||
  (
    contentAttributes.value.gowaGroupInvites ||
    contentAttributes.value.gowa_group_invites ||
    []
  ).find(item => item.code === code);
const conversationIdFor = code =>
  receipt(code)?.conversation_id || receipt(code)?.conversationId;
const statusLabel = code => {
  const state = receipt(code)?.state;
  if (state === 'joined')
    return t('GOWA_GROUP_INVITE.JOINED', {
      group: receipt(code).group_name || receipt(code).groupName,
    });
  if (state === 'requested') return t('GOWA_GROUP_INVITE.REQUESTED');
  if (state === 'unconfirmed') return t('GOWA_GROUP_INVITE.UNCONFIRMED');
  if (state === 'verification_failed')
    return t('GOWA_GROUP_INVITE.VERIFICATION_FAILED');
  return '';
};
const act = async code => {
  if (
    busy.value ||
    !codes.value.includes(code) ||
    (receipt(code)?.state === 'joined' && conversationIdFor(code))
  )
    return;
  busy.value = code;
  errors.value[code] = '';
  try {
    const refresh = Boolean(receipt(code));
    const { data } = await GowaAPI.groupInvite(
      conversationId.value,
      id.value,
      code,
      refresh
    );
    results.value[code] = data.invite;
  } catch (error) {
    const errorCode = error.response?.data?.code;
    const known = [
      'DEVICE_NOT_CONNECTED',
      'NUMBER_MISMATCH',
      'INVALID_INVITE',
      'GROUPS_IGNORED',
    ];
    errors.value[code] = [401, 403].includes(error.response?.status)
      ? t('GOWA_GROUP_INVITE.ERRORS.FORBIDDEN')
      : t(
          known.includes(errorCode)
            ? `GOWA_GROUP_INVITE.ERRORS.${errorCode}`
            : 'GOWA_GROUP_INVITE.ERRORS.DEFAULT'
        );
  } finally {
    busy.value = '';
  }
};
const acceptLink = event => {
  const anchor = event.target.closest('a[href]');
  if (!anchor) return;
  const code = groupInviteCodeFromUrl(anchor.getAttribute('href'));
  if (!codes.value.includes(code)) return;
  event.preventDefault();
  act(code);
};
defineExpose({ acceptLink });
</script>

<template>
  <div
    v-if="codes.length"
    class="flex flex-col gap-2 border-t border-n-weak pt-2"
  >
    <p class="m-0 text-xs text-n-slate-11">
      {{ t('GOWA_GROUP_INVITE.USING_INBOX', { inbox: inbox.name }) }}
    </p>
    <div
      v-for="code in codes"
      :key="code"
      class="flex flex-col items-start gap-1"
    >
      <Button
        v-if="receipt(code)?.state !== 'joined' || !conversationIdFor(code)"
        size="sm"
        type="button"
        :label="
          receipt(code)
            ? t('GOWA_GROUP_INVITE.CHECK')
            : t('GOWA_GROUP_INVITE.JOIN')
        "
        :is-loading="busy === code"
        :disabled="Boolean(busy)"
        @click="act(code)"
      />
      <RouterLink
        v-if="receipt(code)?.state === 'joined' && conversationIdFor(code)"
        :to="`/app/accounts/${accountId}/conversations/${conversationIdFor(code)}`"
        class="text-sm font-medium text-n-teal-11 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
      >
        {{ t('GOWA_GROUP_INVITE.OPEN') }}
      </RouterLink>
      <p
        v-if="statusLabel(code)"
        role="status"
        aria-live="polite"
        class="m-0 text-xs"
        :class="
          receipt(code)?.state === 'joined'
            ? 'text-n-teal-11'
            : 'text-n-amber-11'
        "
      >
        {{ statusLabel(code) }}
      </p>
      <p v-if="errors[code]" role="alert" class="m-0 text-xs text-n-ruby-11">
        {{ errors[code] }}
      </p>
    </div>
  </div>
</template>
